import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixLocaldeliverySpiV1Tpa from './localdelivery-spi-v1-tpa-local-delivery-spi.http.js';
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

export interface Tpa {}

export interface ListAccountIdsRequest {
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
}

export interface ListAccountIdsResponse {
  /** Account IDs. */
  accountIds?: string[];
}

export interface BusinessStatusRequest {
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
  /** Account ID. */
  accountId: string;
}

export interface BusinessStatusResponse {
  /** List of statue. */
  status?: BusinessStatusWithLiterals;
  /** The reason for the error in free text. */
  failureReasonMessage?: string | null;
}

/** Business Status. */
export enum BusinessStatus {
  NONE = 'NONE',
  READY = 'READY',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  NOT_STARTED = 'NOT_STARTED',
}

/** @enumType */
export type BusinessStatusWithLiterals =
  | BusinessStatus
  | 'NONE'
  | 'READY'
  | 'PENDING'
  | 'ERROR'
  | 'NOT_STARTED';

export interface EstimateDeliveryRequest
  extends EstimateDeliveryRequestDispatchTimeOneOf {
  /** Estimated pickup time. */
  pickupTime?: Date | null;
  /** Estimated dropoff time. */
  dropoffTime?: Date | null;
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
  /** Account ID. */
  accountId: string;
  /** Delivery pickup address. */
  pickupAddress?: Address;
  /** Delivery dropoff address. */
  dropoffAddress?: Address;
  /** Unique identifier of the specific store that the delivery is for. */
  storeLocationId: string;
  /** The subtotal for all items in the order, excluding tax/tip. */
  orderTotal: Money;
}

/** @oneof */
export interface EstimateDeliveryRequestDispatchTimeOneOf {
  /** Estimated pickup time. */
  pickupTime?: Date | null;
  /** Estimated dropoff time. */
  dropoffTime?: Date | null;
}

/** Physical address */
export interface Address {
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /** Coordinates of the physical address. */
  location?: AddressLocation;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /** Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative. */
  formattedValue?: string | null;
}

export interface EstimateDeliveryResponse {
  /** Optional. Used as reference to the estimate when creating a new order. */
  _id?: string | null;
  /** Amount that is charged for this delivery. */
  fee?: Money;
  /** Estimated pickup time. */
  promisedPickupTime?: Date | null;
  /** Estimated dropoff time. */
  promisedDropoffTime?: Date | null;
}

export interface CreateDeliveryRequest
  extends CreateDeliveryRequestDispatchTimeOneOf {
  /** The requested time at which the order should be picked up. */
  pickupTime?: Date | null;
  /** The requested time at which the order should be delivered. */
  dropoffTime?: Date | null;
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId: string;
  /** Account ID. */
  accountId: string;
  /** Unique identifier of the specific store that the delivery is for. */
  storeLocationId: string;
  /** The name of the business where the order should be picked up from. */
  pickupBusinessName: string;
  /** Estimate ID. */
  estimateId?: string | null;
  /** Delivery pickup address. */
  pickupAddress: Address;
  /** Delivery dropoff address. */
  dropoffAddress: Address;
  /** Phone number of the store where the order should be picked up from. */
  pickupPhoneNumber: string;
  /** Customer details. */
  customer: Customer;
  /** Instructions for the courier at the pickup location. */
  pickupInstructions?: string | null;
  /** Instructions for the courier at the dropoff location. */
  dropoffInstructions?: string | null;
  /** Is contactless delivery. */
  contactlessDelivery?: boolean | null;
  /** Order ID. */
  orderId: string;
  /** The subtotal for all items in the order, excluding tax/tip. */
  orderTotal: Money;
  /** Amount that will be paid to the courier. */
  orderTip: Money;
}

/** @oneof */
export interface CreateDeliveryRequestDispatchTimeOneOf {
  /** The requested time at which the order should be picked up. */
  pickupTime?: Date | null;
  /** The requested time at which the order should be delivered. */
  dropoffTime?: Date | null;
}

export interface Customer {
  /** First name. */
  firstName?: string;
  /** Last name. */
  lastName?: string;
  /** Phone number. */
  phoneNumber?: string;
  /**
   * Email address.
   * @format EMAIL
   */
  email?: string;
}

export interface CreateDeliveryResponse {
  /** The vendor new order id */
  _id?: string;
  /** When a delivery is ready to be picked up. This is the start of the pickup window. */
  pickupWindowStartTime?: Date | null;
  /** When a delivery must be picked up by. This is the end of the pickup window. */
  pickupWindowEndTime?: Date | null;
  /** When a delivery is ready to be dropped off. This is the start of the dropoff window. */
  dropoffWindowStartTime?: Date | null;
  /** When a delivery must be dropped off by. This is the end of the dropoff window. */
  dropoffWindowEndTime?: Date | null;
  /** Amount that is charged for this delivery. */
  fee?: Money;
}

/** @docsIgnore */
export type EstimateDeliveryApplicationErrors = {
  code?: 'ESTIMATE_DELIVERY_REJECTED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CreateDeliveryApplicationErrors = {
  code?: 'CREATE_DELIVERY_REJECTED';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Retrieves list of account belongs to given instance
 * @param instanceId - Instance ID.
 * @public
 * @documentationMaturity preview
 * @requiredField instanceId
 * @permissionId WIX_RESTAURANTS.READ_LOCAL_DELIVERY
 * @applicableIdentity APP
 * @fqn com.wixpress.localdelivery.spi.v1.LocalDeliveryProvider.ListAccountIds
 */
export async function listAccountIds(
  instanceId: string
): Promise<NonNullablePaths<ListAccountIdsResponse, `accountIds`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    instanceId: instanceId,
  });

  const reqOpts = ambassadorWixLocaldeliverySpiV1Tpa.listAccountIds(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { instanceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['instanceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves the delivery vendor business status
 * @param instanceId - Instance ID.
 * @public
 * @documentationMaturity preview
 * @requiredField instanceId
 * @requiredField options
 * @requiredField options.accountId
 * @permissionId WIX_RESTAURANTS.READ_LOCAL_DELIVERY
 * @applicableIdentity APP
 * @fqn com.wixpress.localdelivery.spi.v1.LocalDeliveryProvider.BusinessStatus
 */
export async function businessStatus(
  instanceId: string,
  options: NonNullablePaths<BusinessStatusOptions, `accountId`, 2>
): Promise<NonNullablePaths<BusinessStatusResponse, `status`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    instanceId: instanceId,
    accountId: options?.accountId,
  });

  const reqOpts = ambassadorWixLocaldeliverySpiV1Tpa.businessStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0]',
          accountId: '$[1].accountId',
        },
        singleArgumentUnchanged: false,
      },
      ['instanceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BusinessStatusOptions {
  /** Account ID. */
  accountId: string;
}

/**
 * Retrieves an estimate of the delivery
 * @param instanceId - Instance ID.
 * @public
 * @documentationMaturity preview
 * @requiredField instanceId
 * @requiredField options.accountId
 * @requiredField options.dispatchTime
 * @requiredField options.orderTotal
 * @requiredField options.storeLocationId
 * @permissionId WIX_RESTAURANTS.READ_LOCAL_DELIVERY
 * @applicableIdentity APP
 * @fqn com.wixpress.localdelivery.spi.v1.LocalDeliveryProvider.EstimateDelivery
 */
export async function estimateDelivery(
  instanceId: string,
  options?: NonNullablePaths<
    EstimateDeliveryOptions,
    `accountId` | `orderTotal` | `storeLocationId`,
    2
  >
): Promise<
  NonNullablePaths<
    EstimateDeliveryResponse,
    `fee.value` | `fee.currency`,
    3
  > & {
    __applicationErrorsType?: EstimateDeliveryApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      instanceId: instanceId,
      accountId: options?.accountId,
      pickupAddress: options?.pickupAddress,
      dropoffAddress: options?.dropoffAddress,
      pickupTime: options?.pickupTime,
      dropoffTime: options?.dropoffTime,
      storeLocationId: options?.storeLocationId,
      orderTotal: options?.orderTotal,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupAddress' }, { path: 'dropoffAddress' }],
      },
    ]
  );

  const reqOpts = ambassadorWixLocaldeliverySpiV1Tpa.estimateDelivery(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0]',
          accountId: '$[1].accountId',
          pickupAddress: '$[1].pickupAddress',
          dropoffAddress: '$[1].dropoffAddress',
          pickupTime: '$[1].pickupTime',
          dropoffTime: '$[1].dropoffTime',
          storeLocationId: '$[1].storeLocationId',
          orderTotal: '$[1].orderTotal',
        },
        singleArgumentUnchanged: false,
      },
      ['instanceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EstimateDeliveryOptions
  extends EstimateDeliveryOptionsDispatchTimeOneOf {
  /** Account ID. */
  accountId: string;
  /** Delivery pickup address. */
  pickupAddress?: Address;
  /** Delivery dropoff address. */
  dropoffAddress?: Address;
  /** Estimated pickup time. */
  pickupTime?: Date | null;
  /** Estimated dropoff time. */
  dropoffTime?: Date | null;
  /** Unique identifier of the specific store that the delivery is for. */
  storeLocationId: string;
  /** The subtotal for all items in the order, excluding tax/tip. */
  orderTotal: Money;
}

/** @oneof */
export interface EstimateDeliveryOptionsDispatchTimeOneOf {
  /** Estimated pickup time. */
  pickupTime?: Date | null;
  /** Estimated dropoff time. */
  dropoffTime?: Date | null;
}

/**
 * Creates a new delivery order
 * @param instanceId - Instance ID.
 * @public
 * @documentationMaturity preview
 * @requiredField instanceId
 * @requiredField options.accountId
 * @requiredField options.customer
 * @requiredField options.customer.email
 * @requiredField options.customer.firstName
 * @requiredField options.customer.lastName
 * @requiredField options.customer.phoneNumber
 * @requiredField options.dispatchTime
 * @requiredField options.dropoffAddress
 * @requiredField options.orderId
 * @requiredField options.orderTip
 * @requiredField options.orderTotal
 * @requiredField options.pickupAddress
 * @requiredField options.pickupBusinessName
 * @requiredField options.pickupPhoneNumber
 * @requiredField options.storeLocationId
 * @permissionId WIX_RESTAURANTS.WRITE_LOCAL_DELIVERY
 * @applicableIdentity APP
 * @fqn com.wixpress.localdelivery.spi.v1.LocalDeliveryProvider.CreateDelivery
 */
export async function createDelivery(
  instanceId: string,
  options?: NonNullablePaths<
    CreateDeliveryOptions,
    | `accountId`
    | `customer`
    | `customer.email`
    | `customer.firstName`
    | `customer.lastName`
    | `customer.phoneNumber`
    | `dropoffAddress`
    | `orderId`
    | `orderTip`
    | `orderTotal`
    | `pickupAddress`
    | `pickupBusinessName`
    | `pickupPhoneNumber`
    | `storeLocationId`,
    3
  >
): Promise<
  NonNullablePaths<
    CreateDeliveryResponse,
    `_id` | `fee.value` | `fee.currency`,
    3
  > & {
    __applicationErrorsType?: CreateDeliveryApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      instanceId: instanceId,
      accountId: options?.accountId,
      storeLocationId: options?.storeLocationId,
      pickupBusinessName: options?.pickupBusinessName,
      estimateId: options?.estimateId,
      pickupTime: options?.pickupTime,
      dropoffTime: options?.dropoffTime,
      pickupAddress: options?.pickupAddress,
      dropoffAddress: options?.dropoffAddress,
      pickupPhoneNumber: options?.pickupPhoneNumber,
      customer: options?.customer,
      pickupInstructions: options?.pickupInstructions,
      dropoffInstructions: options?.dropoffInstructions,
      contactlessDelivery: options?.contactlessDelivery,
      orderId: options?.orderId,
      orderTotal: options?.orderTotal,
      orderTip: options?.orderTip,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupAddress' }, { path: 'dropoffAddress' }],
      },
    ]
  );

  const reqOpts = ambassadorWixLocaldeliverySpiV1Tpa.createDelivery(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0]',
          accountId: '$[1].accountId',
          storeLocationId: '$[1].storeLocationId',
          pickupBusinessName: '$[1].pickupBusinessName',
          estimateId: '$[1].estimateId',
          pickupTime: '$[1].pickupTime',
          dropoffTime: '$[1].dropoffTime',
          pickupAddress: '$[1].pickupAddress',
          dropoffAddress: '$[1].dropoffAddress',
          pickupPhoneNumber: '$[1].pickupPhoneNumber',
          customer: '$[1].customer',
          pickupInstructions: '$[1].pickupInstructions',
          dropoffInstructions: '$[1].dropoffInstructions',
          contactlessDelivery: '$[1].contactlessDelivery',
          orderId: '$[1].orderId',
          orderTotal: '$[1].orderTotal',
          orderTip: '$[1].orderTip',
        },
        singleArgumentUnchanged: false,
      },
      ['instanceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDeliveryOptions
  extends CreateDeliveryOptionsDispatchTimeOneOf {
  /** Account ID. */
  accountId: string;
  /** Unique identifier of the specific store that the delivery is for. */
  storeLocationId: string;
  /** The name of the business where the order should be picked up from. */
  pickupBusinessName: string;
  /** Estimate ID. */
  estimateId?: string | null;
  /** The requested time at which the order should be picked up. */
  pickupTime?: Date | null;
  /** The requested time at which the order should be delivered. */
  dropoffTime?: Date | null;
  /** Delivery pickup address. */
  pickupAddress: Address;
  /** Delivery dropoff address. */
  dropoffAddress: Address;
  /** Phone number of the store where the order should be picked up from. */
  pickupPhoneNumber: string;
  /** Customer details. */
  customer: Customer;
  /** Instructions for the courier at the pickup location. */
  pickupInstructions?: string | null;
  /** Instructions for the courier at the dropoff location. */
  dropoffInstructions?: string | null;
  /** Is contactless delivery. */
  contactlessDelivery?: boolean | null;
  /** Order ID. */
  orderId: string;
  /** The subtotal for all items in the order, excluding tax/tip. */
  orderTotal: Money;
  /** Amount that will be paid to the courier. */
  orderTip: Money;
}

/** @oneof */
export interface CreateDeliveryOptionsDispatchTimeOneOf {
  /** The requested time at which the order should be picked up. */
  pickupTime?: Date | null;
  /** The requested time at which the order should be delivered. */
  dropoffTime?: Date | null;
}
